<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Edit User
            </h3>

            <p>
              Enter the details of the user. If an accounting system reference is added, the user will be
              shown on the invoices in the accounting system that he or she is responsible for.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <text-form-group
                    v-model="data.first_name"
                    name="first_name"
                    label="First Name"
                    help-text="The first name of the user."
                    :error="getError('first_name')"
                ></text-form-group>

                <text-form-group
                    v-model="data.last_name"
                    name="last_name"
                    label="Last Name"
                    help-text="The last name of the user."
                    :error="getError('last_name')"
                ></text-form-group>

                <text-form-group
                    v-model="data.initials"
                    name="initials"
                    label="Initials"
                    help-text="The initials of the user."
                    :error="getError('initials')"
                ></text-form-group>

                <text-form-group
                    v-model="data.title"
                    name="title"
                    label="Title"
                    help-text="The title of the user."
                    :error="getError('title')"
                ></text-form-group>

                <email-form-group
                    v-model="data.email"
                    name="email"
                    label="Email"
                    help-text="The user's email."
                    :error="getError('email')"
                ></email-form-group>

                <text-form-group
                    v-model="data.phone"
                    name="phone"
                    label="Phone Number"
                    help-text="The phone number of the user."
                    :error="getError('phone')"
                ></text-form-group>

                <text-form-group
                    v-model="data.linkedin"
                    name="linkedin"
                    label="LinkedIn"
                    help-text="A link to the user's LinkedIn profile."
                    :error="getError('linkedin')"
                ></text-form-group>

                <color-form-group
                    v-model="data.color"
                    name="color"
                    label="Color"
                    help-text="The color of the user."
                    :colors="colors.data"
                    :error="getError('color')"
                ></color-form-group>

                <select-form-group
                    v-model="data.accounting_system_number"
                    name="accounting_system_number"
                    label="Accounting System Reference"
                    help-text="The employee in the accounting system that corresponds to this user."
                    :error="getError('accounting_system_number')"
                    :options="accountingSystemNumberOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="data.email_tracking_account_reference"
                    name="email_tracking_account_reference"
                    label="Email Tracking Account"
                    help-text="The account in the email tracking system that corresponds to this user."
                    :error="getError('email_tracking_account_reference')"
                    :options="emailTrackingAccountOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="data.slack_reference"
                    name="slack_reference"
                    label="Slack User"
                    help-text="The Slack user that corresponds to this user."
                    :error="getError('slack_reference')"
                    :options="slackUserOptions"
                    :nullable="true"
                ></select-form-group>

                <radio-form-group
                    v-model="data.is_default_subscription_responsible"
                    name="is_default_subscription_responsible"
                    label="Default Subscription Responsible"
                    help-text="A flag indicating whether the user is the default responsible for new subscriptions."
                    :error="getError('is_default_subscription_responsible')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                ></radio-form-group>

                <gallery-form-group
                    v-model="data.image_id"
                    name="image_id"
                    label="Image"
                    :error="getError('image_id')"
                    :nullable="true"
                ></gallery-form-group>

                <radio-form-group
                    v-model="data.is_active"
                    name="is_active"
                    label="Active"
                    help-text="A flag indicating whether the user is active."
                    :error="getError('is_active')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                ></radio-form-group>

                <hr>

                <submit-form-group button-class="btn-black"></submit-form-group>
              </form>
            </div>
          </div>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Roles
            </h3>

            <p>
              This table displays the roles that the user has. There is only one role in the system: the
              admin role. If the user has the admin role, he or she can view anything in the system. If
              the user does not have the admin role, the access will be more limited.
            </p>

            <button
                type="button"
                class="btn btn-sm btn-black"
                data-toggle="modal"
                data-target="#create-user-role-modal"
            >
              <i class="fa fa-plus"></i> Add Role
            </button>
          </help-text>
        </div>

        <div class="col-xs-18">
          <UserRoleList :user="user" @input="$emit('user-updated', $event)"/>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Ad Commission
            </h3>

            <p>
              This table displays the channels where the user will be rewarded a commission of each ad sale.
            </p>

            <button
                type="button"
                class="btn btn-sm btn-black"
                data-toggle="modal"
                data-target="#create-ad-commission-agreement-modal"
            >
              <i class="fa fa-plus"></i> Add Agreement
            </button>
          </help-text>
        </div>

        <div class="col-xs-18">
          <AdCommissionAgreementList :user="user"/>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Subscription Commission
            </h3>

            <p>
              This table displays the portals where the user will be rewarded a commission of each subscription sale.
            </p>

            <button
                type="button"
                class="btn btn-sm btn-black"
                data-toggle="modal"
                data-target="#create-subscription-commission-agreement-modal"
            >
              <i class="fa fa-plus"></i> Add Agreement
            </button>
          </help-text>
        </div>

        <div class="col-xs-18">
          <SubscriptionCommissionAgreementList :user="user"/>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Commission Items
            </h3>

            <p>
              This table displays the commission items that are attached to this user. Commission items
              may be used to adjust the commission for a user manually.
            </p>

            <button
                type="button"
                class="btn btn-sm btn-black"
                data-toggle="modal"
                data-target="#create-commission-item-modal"
            >
              <i class="fa fa-plus"></i> Add Commmission Item
            </button>
          </help-text>
        </div>

        <div class="col-xs-18">
          <commission-item-list :user="user"></commission-item-list>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import AdCommissionAgreementList from '@/components/Administration/AdCommissionAgreements/Panels/List'
import ColorFormGroup from '@/components/FormGroups/Horizontal/Color'
import CommissionItemList from '@/components/Administration/CommissionItems/Panels/List'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import GalleryFormGroup from '@/components/FormGroups/Horizontal/Gallery'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import SubscriptionCommissionAgreementList from '@/components/Administration/SubscriptionCommissionAgreements/Panels/List'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import UserRoleList from '@/components/Administration/UserRoles/Panels/List'

export default {
  components: {
    AdCommissionAgreementList,
    ColorFormGroup,
    CommissionItemList,
    EmailFormGroup,
    HelpText,
    GalleryFormGroup,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    SubscriptionCommissionAgreementList,
    TextFormGroup,
    UserRoleList
  },

  mixins: [Errors, Load],

  model: {
    prop: 'user',
    event: 'user-updated'
  },

  props: ['user'],

  data() {
    return {
      data: {...this.user.attributes},

      accountingSystemEmployees: null,
      emailTrackingAccounts: null,
      slackUsers: null,
      colors: null,

      shared: store
    }
  },

  mounted() {
    this.load({
      accountingSystemEmployees: '/api/v2/bookkeeping-employees',
      emailTrackingAccounts: '/api/v2/email-tracking-accounts',
      slackUsers: '/api/v2/slack-users',
      colors: '/api/v2/colors'
    })
  },

  computed: {
    accountingSystemNumberOptions() {
      return this.accountingSystemEmployees.data.map(accountingSystemEmployee => {
        return {
          value: accountingSystemEmployee.id,
          label: accountingSystemEmployee.attributes.name
        }
      })
    },

    emailTrackingAccountOptions() {
      return this.emailTrackingAccounts.data.map(emailTrackingAccount => {
        return {
          value: emailTrackingAccount.id,
          label: emailTrackingAccount.attributes.name
        }
      })
    },

    slackUserOptions() {
      return this.slackUsers.data.map(slackUser => {
        return {
          value: slackUser.id,
          label: slackUser.attributes.name
        }
      })
    }
  },

  methods: {
    submit() {
      http.put('/api/v2/users/' + this.user.id, this.data)
          .then(response => {
            notify.success('The user was saved')

            this.errors = {}

            this.$emit('user-updated', response.data.data)
          })
          .catch(this.catchValidationErrors)
    },
  }
}
</script>
